import {axiYoutube, GetListOutput, GetListParams} from '@shared/api';
import {validateData} from '@shared/utils';
import {
  EventTemplate,
  TemplateParameterValue,
  Video,
  VideoEventSchedule,
} from '../types';
import {
  templateParameterValueV8nSchema,
  v8nSchemaOfGetEventTemplatesResponse,
  v8nSchemaOfGetVideoSchedulesResponse,
  v8nSchemaOfGetVideosResponse,
} from './backendSchemas';
import {
  GetEventTemplatesResponse,
  GetVideoSchedulesResponse,
  GetVideosResponse,
  TemplateParameterValueOnBackend,
} from './backendTypes';
import {
  mapEventTemplateFromBackend,
  mapTemplateParameterValueFromBackend,
  mapVideoEventScheduleFromBackend,
  mapVideoFromBackend,
} from './dataMappers';

export interface GetVideoSchedulesParams extends GetListParams {
  videoId?: string;
  templateId?: string;
}

export interface CreateVideoEventScheduleParams {
  videoId: string;
  templateId: string;
  cron: string;
  durationMinutes: number;
  runImmediately?: boolean;
}

export interface ResolveVideoParams {
  videoUrl: string;
  channel: string;
}

export const getVideos = async ({page, pageSize}: GetListParams = {}): Promise<
  GetListOutput<Video>
> => {
  const response = await axiYoutube.get<GetVideosResponse>('/admin/videos', {
    params: {
      page,
      per_page: pageSize,
    },
  });

  try {
    validateData(response.data, v8nSchemaOfGetVideosResponse, 'getVideos');
  } catch {
    //
  }

  const {
    array,
    total,
    total_pages,
    page: pageFromBackend,
    per_page,
  } = response.data;
  const items = array?.map(item => mapVideoFromBackend(item)) ?? [];

  return {
    items,
    page: pageFromBackend,
    pageSize: per_page,
    total,
    totalPages: total_pages,
  };
};

export const resolveVideo = async ({
  videoUrl,
  channel,
}: ResolveVideoParams): Promise<TemplateParameterValue> => {
  const response = await axiYoutube.get<TemplateParameterValueOnBackend>(
    '/admin/videos/templates/parameters',
    {
      params: {
        video_url: videoUrl,
        channel,
      },
    },
  );

  try {
    validateData(
      response.data,
      templateParameterValueV8nSchema,
      'resolveVideo',
    );
  } catch {
    //
  }

  return mapTemplateParameterValueFromBackend(response.data);
};

export const getEventTemplates = async (): Promise<EventTemplate[]> => {
  const response = await axiYoutube.get<GetEventTemplatesResponse>(
    '/admin/events/templates',
  );

  try {
    validateData(
      response.data,
      v8nSchemaOfGetEventTemplatesResponse,
      'getEventTemplates',
    );
  } catch {
    //
  }

  return (response.data ?? []).map(item => mapEventTemplateFromBackend(item));
};

export const getVideoSchedules = async ({
  videoId,
  templateId,
  page,
  pageSize,
}: GetVideoSchedulesParams): Promise<GetListOutput<VideoEventSchedule>> => {
  const response = await axiYoutube.get<GetVideoSchedulesResponse>(
    '/admin/videos/events/schedules',
    {
      params: {
        video_id: videoId,
        template_id: templateId,
        page,
        per_page: pageSize,
      },
    },
  );

  try {
    validateData(
      response.data,
      v8nSchemaOfGetVideoSchedulesResponse,
      'getVideoSchedules',
    );
  } catch {
    //
  }

  const {
    array,
    total,
    total_pages,
    page: pageFromBackend,
    per_page,
  } = response.data;
  const items =
    array?.map(item => mapVideoEventScheduleFromBackend(item)) ?? [];

  return {
    items,
    page: pageFromBackend,
    pageSize: per_page,
    total,
    totalPages: total_pages,
  };
};

export const createVideoEventSchedule = async ({
  videoId,
  templateId,
  cron,
  durationMinutes,
  runImmediately,
}: CreateVideoEventScheduleParams): Promise<void> => {
  await axiYoutube.post('/admin/videos/events/schedules', {
    video_id: videoId,
    template_id: templateId,
    cron,
    duration_minutes: durationMinutes,
    run_immediately: runImmediately,
  });
};

export const deleteVideoEventSchedule = async (
  scheduleId: string,
): Promise<void> => {
  await axiYoutube.delete(`/admin/videos/events/schedules/${scheduleId}`);
};
